import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { resolveImageUrl } from '../../lib/imageUrl';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, X, ExternalLink, Building2 } from 'lucide-react';
import ImageUploader from '../../components/admin/ImageUploader';

const empty = { name: '', logoUrl: '', websiteUrl: '', order: 0 };

const PartnersManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/partners');
      setItems(data);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...empty, order: items.length }); setEditing({}); };
  const openEdit = (it) => { setForm({ ...it }); setEditing(it); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Le nom est requis'); return; }
    try {
      const payload = {
        name: form.name.trim(),
        logoUrl: form.logoUrl || null,
        websiteUrl: form.websiteUrl || null,
        order: Number(form.order) || 0,
      };
      if (editing && editing.id) {
        await api.patch(`/admin/partners/${editing.id}`, payload);
        toast.success('Partenaire mis à jour');
      } else {
        await api.post('/admin/partners', payload);
        toast.success('Partenaire ajouté');
      }
      close();
      load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    try {
      await api.delete(`/admin/partners/${id}`);
      toast.success('Partenaire supprimé');
      load();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div data-testid="admin-partners-manager">
      <div className="flex flex-wrap justify-between items-center gap-6 mb-10">
        <div>
          <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-2 block">Cercle de confiance</span>
          <h2 className="text-4xl font-serif text-[#111111]">Partenaires</h2>
          <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl">
            Ajoutez vos partenaires & prestataires affichés en bas de la page d'accueil.
            Vous pouvez mettre <strong>un logo</strong> (recommandé) <em>ou simplement le nom</em>.
          </p>
        </div>
        <button
          onClick={openNew}
          data-testid="partner-add-btn"
          className="w-full sm:w-auto justify-center flex items-center gap-3 px-6 py-3 border border-[#111111] text-xs uppercase tracking-[0.3em] hover:bg-[#333333] hover:border-[#111111] hover:text-white transition-all whitespace-nowrap"
        >
          <Plus className="w-4 h-4" /> Ajouter un partenaire
        </button>
      </div>

      {loading ? (
        <p className="text-center text-[#6B6B6B] py-20">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#111111]/20">
          <Building2 className="w-10 h-10 text-[#6B6B6B] mx-auto mb-4" strokeWidth={1} />
          <p className="text-[#6B6B6B]">Aucun partenaire — la section ne sera pas affichée.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p) => (
            <div key={p.id} className="border border-[#111111]/10 bg-white p-6 group" data-testid={`partner-item-${p.id}`}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#111111]">Ordre {p.order}</p>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(p)} data-testid={`partner-edit-${p.id}`} className="w-8 h-8 flex items-center justify-center border border-[#111111]/20 hover:border-[#111111]">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => del(p.id)} data-testid={`partner-delete-${p.id}`} className="w-8 h-8 flex items-center justify-center border border-[#111111]/20 hover:border-red-500 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="h-24 flex items-center justify-center bg-[#F5F5F5] border border-[#111111]/10 mb-4">
                {p.logoUrl ? (
                  <img src={resolveImageUrl(p.logoUrl)} alt={p.name} className="max-h-16 max-w-[80%] object-contain" />
                ) : (
                  <span className="font-serif text-base text-[#6B6B6B]">{p.name}</span>
                )}
              </div>

              <h3 className="font-serif text-lg text-[#111111] truncate">{p.name}</h3>
              {p.websiteUrl && (
                <a href={p.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#111111] hover:underline inline-flex items-center gap-1 mt-1">
                  {p.websiteUrl.replace(/^https?:\/\//, '').slice(0, 30)}{p.websiteUrl.length > 35 ? '…' : ''} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={close}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-xl p-10 border-t-4 border-[#111111] relative" data-testid="partner-form">
            <button type="button" onClick={close} className="absolute top-5 right-5 text-[#6B6B6B] hover:text-[#111111]">
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-2xl font-serif mb-8 text-[#111111]">
              {editing.id ? 'Modifier le partenaire' : 'Nouveau partenaire'}
            </h3>
            <div className="space-y-5">
              <Field label="Nom du partenaire" required>
                <input
                  data-testid="partner-form-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Maison Margaux"
                  className="luxury-input-light"
                />
              </Field>

              <Field label="Logo (recommandé)">
                <ImageUploader
                  value={form.logoUrl}
                  onChange={(v) => setForm({ ...form, logoUrl: v })}
                  testId="partner-form-logo"
                />
                <p className="text-[10px] text-[#6B6B6B]/70 italic mt-2">
                  Préférez un logo sur fond transparent (PNG/WEBP). Si vide, le nom du partenaire sera affiché en typographie.
                </p>
              </Field>

              <Field label="Site web (optionnel)">
                <input
                  data-testid="partner-form-website"
                  type="url"
                  value={form.websiteUrl || ''}
                  onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
                  placeholder="https://example.com"
                  className="luxury-input-light"
                />
              </Field>

              <Field label="Ordre d'affichage">
                <input
                  data-testid="partner-form-order"
                  type="number"
                  value={form.order || 0}
                  onChange={(e) => setForm({ ...form, order: e.target.value })}
                  className="luxury-input-light"
                />
              </Field>
            </div>
            <div className="flex gap-4 mt-8">
              <button type="button" onClick={close} className="px-6 py-3 text-xs uppercase tracking-[0.3em] border border-[#111111]/30 hover:bg-[#F5F5F5]">Annuler</button>
              <button type="submit" data-testid="partner-form-submit" className="flex-1 px-6 py-3 text-xs uppercase tracking-[0.3em] bg-[#111111] text-white hover:bg-[#333333] transition-colors">
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-2 block">
      {label}{required && <span className="text-[#111111]"> *</span>}
    </span>
    {children}
  </label>
);

export default PartnersManager;
